import React, { useContext } from 'react';
import { useRouter } from 'next/router';
import { IDesignerProfile } from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import UserContext from '@context/User/user-context';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import ShareModalContext, { SHARETYPE } from '@context/Modal/share-modal-context';
import If from '@components/shared/conditional/If/If';
import ShareButton from '@components/shared/buttons/ShareButton/ShareButton';
import env from '@lib/envs/env';
import { appendThumborTransformQueryString } from '@lib/image';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import SaveButton from '@components/shared/buttons/SaveButton/SaveButton';
import _ from 'lodash';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';
import EditIcon from '@components/shared/images/atoms/EditIcon/EditIcon';
import ButtonPrimary from '@components/shared/buttons/ButtonPrimary/ButtonPrimary';
import DesignerAvailableText from '@components/shared/DesignerAvailableText/DesignerAvailableText';

import styles from './DesignerIntro.module.scss';

const SAVE_DESIGNER_MUTATION = gql`
mutation SaveDesigner($designerId: ID!, $opinion: Int!) {
  setUserOpinionDesigner(
    designer: $designerId
    opinion: $opinion
  ) {
    id
  }
}
`;
interface IDesignerIntroProps {
  allowEdit?: boolean;
  designer: IDesignerProfile;
  editing?: boolean;
  toggleEditing?: () => void;
}

const DesignerIntro = ({
  allowEdit = false,
  designer,
  editing = false,
  toggleEditing = () => {},
}: IDesignerIntroProps) => {
  const alert = useAlert(TYPES.SUCCESS);

  const { user } = useContext(UserContext);
  const router = useRouter();

  const { open: openLoginRegister } = useContext(LoginRegisterModalContext);
  const { open: openShare } = useContext(ShareModalContext);

  const [setDesignerOpinion] = useMutation(SAVE_DESIGNER_MUTATION, {

  });

  const saveDesigner = async () => {
    // Opinions: null ==> No opinion, 1 ==> Like, -1 ==> Dislike
    const newOpinion = designer.userOpinion === 1 ? null : 1;

    await setDesignerOpinion({
      variables: {
        designerId: designer.id,
        opinion: newOpinion,

      }
    });

    if (newOpinion) {
      alert('This designer has been saved.', 3000);
    }
  };

  const onSaveDesigner = async () => {
    if (!user) {
      openLoginRegister({
        source: 'designer_profile',
        async onLoginSuccess() {
          await saveDesigner();
        },
        async onRegisterSuccess() {
          await saveDesigner();
          window.location.href = '/interior-design-ideas/style-quiz';
        },
        magicLinkRedirectUrl: router.asPath, // Redirect back to the current page
      });
    } else if (user.role === 'customer') {
      await saveDesigner();
    }
  };

  return (
    <>
      <div className={ styles.Root }>
        <div className={ styles.AvatarColumn }>
          <div className={ `${styles.AvatarContainer} ${styles.MobileAvatarContainer}` }>
            <img
              alt={ getProfilePhotoAltText(designer) }
              src={ appendThumborTransformQueryString({
                url: designer.photo,
                width: 70,
                height: 70,
              }) }
            />
          </div>

          <div className={ `${styles.AvatarContainer} ${styles.DesktopAvatarContainer}` }>
            <img
              alt={ getProfilePhotoAltText(designer) }
              src={ appendThumborTransformQueryString({
                url: designer.photo,
                width: 120,
                height: 120,
              }) }
            />
          </div>
        </div>

        <div className={ styles.DesignerInfoTop }>
          <div className={ styles.DesignerTop }>
            <div className={ styles.DesignerPersonalInfo }>
              <h1 data-test="interior-designers-designer" className={ styles.DesignerName }>{ designer.firstName }</h1>
              <If condition={!!designer.pronouns}>
                <span className={ styles.UserPronouns }>({ designer.pronouns })</span>
              </If>
            </div>
            <If condition={ !user || user.role === 'customer' }>
              <SaveButton
                className={ styles.DesignerSave }
                onClick={ _.debounce(onSaveDesigner, 200, { leading: true }) }
                saved={ designer.userOpinion === 1 }
              />

              <ShareButton
                className={ styles.DesignerShare }
                onClick={ () => openShare({
                  shareType: SHARETYPE.DESIGNER,
                  modelId: Number(designer.id),
                  url: `https://${env.DOMAIN_NAME}${designer.profileUri}`
                })}
              />
            </If>
          </div>
          <If condition={ !!designer.location }>
            <div className={ styles.DesignerLocation }>
              {designer.location }
            </div>
          </If>
          <div className={ styles.DesignerAvailability }>
            <DesignerAvailableText designer={designer} />
          </div>
        </div>
      </div>

      <If condition={ allowEdit }>
        <ButtonPrimary
          className={ styles.EditButton }
          disabled={ false }
          onClick={ toggleEditing }
        >
          <If condition={ !editing }>
            <i className={ styles.EditIcon }>
              <EditIcon />
            </i>
            Edit profile
          </If>
          <If condition={ editing }>
            Done
          </If>
        </ButtonPrimary>
      </If>

    </>
  );
};

export default DesignerIntro;

function getProfilePhotoAltText(designer: IDesignerProfile) {
  const { firstName, lastName, location } = designer;
  const _firstName = firstName ? `${firstName.trim()} ` : '';
  const _lastName = lastName ? `${lastName.trim()} ` : '';
  const _location = location ? `${location.trim()} ` : '';
  return `${_firstName}${_lastName}${_location}Havenly Interior Designer`; // eslint-disable-line max-len
}
